import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as ClearFilter } from '../../../assets/icons/filter-close.svg';
import { ReactComponent as Filter } from '../../../assets/icons/filter.svg';
import { ReactComponent as LargeFontIcon } from '../../../assets/icons/table-component/large-font-icon.svg';
import { ReactComponent as MediumFontIcon } from '../../../assets/icons/table-component/medium-font-icon.svg';
import { ReactComponent as SmallFontIcon } from '../../../assets/icons/table-component/small-font-icon.svg';
import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import { useAppDispatch } from '../../../redux/hooks';
import MyButton from '../../button/Button';
import CustomInput from '../../custom-input/CustomInput';
import CustomPopover from '../../custom-pop-over/CustomPopover';
import TableFieldAllow from '../../table-field-allow/TableFieldAllow';

import CustomTableFilter from './TableFilters';

interface CustomTableProperties {
  currentFontSize?: string;
  sizeChangeButtonRequired?: boolean;
  sizeChangeFunction?: () => void;
  rightTopContent?: JSX.Element;
  leftTopContent?: JSX.Element;
  filters?: CommonFilterProperties[];
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListLoading: boolean;
  filterListToDispatch?: AsyncThunk<any, void, any>;
  labelForHideColumn?: {
    label: string;
    accessorKey: any;
    disableHideFunc: boolean;
  }[];
  hidedColumn?: string[];
  setHidedColumn?: React.Dispatch<React.SetStateAction<string[]>>;
  refreshRequired?: boolean;
  handleRefresh?: () => void;
  hoverLabel?: string;
}

/**
 * Function to handle table filters
 * @returns
 */
function CustomTableHeader(properties: CustomTableProperties) {
  const { t } = useTranslation('sharedComponent');
  const {
    currentFontSize,
    sizeChangeButtonRequired,
    rightTopContent,
    leftTopContent,
    sizeChangeFunction,
    filters,
    filterListLoading,
    filtersToDispatch,
    filterListToDispatch,
    labelForHideColumn,
    hidedColumn,
    setHidedColumn,
    refreshRequired,
    handleRefresh,
    hoverLabel,
  } = properties;

  const dispatch = useAppDispatch();

  // As of now there should be only one search by text filter implemented
  const [searchFilter, setSearchFilter] = useState<CommonFilterProperties>();
  const [valueToShow, setValueToShow] = useState<string | number>('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  /**
   * Function to decide font change to show
   * @returns JSX.Element as an svg icon
   */
  const handleFontChangeIcon = (): JSX.Element => {
    let icon;
    switch (currentFontSize) {
      case 'large':
        icon = <LargeFontIcon />;
        break;
      case 'medium':
        icon = <MediumFontIcon />;
        break;
      default:
        icon = <SmallFontIcon />;
        break;
    }
    return icon;
  };

  /**
   * Function to remove filter from the list of selected filters
   * @param key
   */
  const handleRemoveFilterByKey = (key: string) => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key,
          value: '', // Empty value as we are using same dispatch function for filter updates
          type: 'remove',
        }),
      );
    }
  };

  /**
   * Function to remove filter from the list of selected filters
   * @param key
   */
  const handleClearFilters = () => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: '', // Empty key as we are using same dispatch function for filter updates
          value: '', // Empty value as we are using same dispatch function for filter updates
          type: 'clear',
        }),
      );
    }
  };

  /**
   * Function to dispatch the updated filters
   * @param name
   * @param value
   */
  const handleChangeFilters = (name: string, value: string | number) => {
    setValueToShow(value);
  };

  /**
   * Handle size change function
   * @returns
   */
  const handleChangeFilter = () => {
    if (!sizeChangeFunction) return;
    sizeChangeFunction();
  };

  useEffect(() => {
    if (filters) {
      const searchBy = filters.find((filter) => filter.searchBy);
      setSearchFilter(searchBy);
      setValueToShow(searchBy?.value.toString() || '');
    }
  }, [filters]);

  // Debounce functionality with 1s delay
  useEffect(() => {
    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set a new debounce timer
    debounceTimer.current = setTimeout(() => {
      const searchBy = filters?.find((filter) => filter.searchBy);
      const trimmedValue = valueToShow.toString().trim();
      if (filtersToDispatch && searchBy?.value.toString() !== trimmedValue) {
        dispatch(
          filtersToDispatch({
            key: 'searchBy',
            value: trimmedValue || '',
            type: 'add',
          }),
        );
      }
    }, 1000); // 1 second

    // Cleanup on unmount or valueToShow change
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [valueToShow]);

  return (
    <div className="custom-table-header-wrap">
      <div className="custom-table-header-first-row">
        <div className="custom-table-left-wrap">
          {Array.isArray(filters) && filtersToDispatch && (
            <div className="custom-table-header-filter-wrap">
              <div className="custom-table-filter">
                <CustomPopover
                  clickableElement={
                    <MyButton
                      label=""
                      buttonType="primary-outline"
                      onClickFunc={() => {}}
                      svgIcon={<Filter />}
                    />
                  }
                  popOverContent={
                    <CustomTableFilter
                      filters={filters}
                      filterListLoading={filterListLoading}
                      filtersToDispatch={filtersToDispatch}
                      filterListToDispatch={filterListToDispatch}
                    />
                  }
                />
              </div>
              <MyButton
                label={t('clearFilter')}
                buttonType="primary-outline"
                onClickFunc={handleClearFilters}
                svgBackIcon={<ClearFilter />}
              />
            </div>
          )}
          {searchFilter ? (
            <CustomInput
              inputLabel=""
              type="text"
              placeholder={t (searchFilter.name)}
              name={searchFilter.key}
              value={valueToShow.toString()} // Because value might be array of string
              onChangeValue={handleChangeFilters}
            />
          ) : null}
          {leftTopContent && <div className="custom-table-left-container">{leftTopContent}</div>}
        </div>
        <div className="custom-table-right-wrap">
          {rightTopContent && <div className="custom-table-right-container">{rightTopContent}</div>}
          <div className="custom-table-size-change">
            {refreshRequired && (
              <MyButton
                label=""
                buttonType="none"
                onClickFunc={handleRefresh}
                svgBackIcon={<RefreshIcon />}
                hoverLabel={hoverLabel}
              />
            )}
            {sizeChangeButtonRequired && (
              <MyButton
                label=""
                buttonType="outline"
                onClickFunc={handleChangeFilter}
                svgIcon={handleFontChangeIcon()}
              />
            )}
          </div>
          {hidedColumn && setHidedColumn && labelForHideColumn && (
            <TableFieldAllow
              labelForHideColumn={labelForHideColumn}
              hidedColumn={hidedColumn}
              setHidedColumn={setHidedColumn}
            />
          )}
        </div>
      </div>
      {filters?.filter((filter) => !filter.searchBy).some((filter) => filter.applyFilter) && (
        <div className="custom-table-header-second-row">
          {filters
            .filter((filter) => !filter.searchBy)
            .map((filter) => {
              let filterTag = null;
              if (
                filter.applyFilter &&
                (filter.type === filterType.DATE_RANGE || filter.type === filterType.TIME_RANGE)
              ) {
                filterTag = (
                  <div className={`filter-tag-wrap ${filter.type && 'dateRange'}`}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details-date">
                      <p>{t(filter.name)}: </p>
                      <p>{filter.value1}</p>
                      <p>-</p>
                      <p>{filter.value2}</p>
                    </div>
                    {filter?.disableClearFilter ? (
                      <span
                        className="close-icon disable"
                        title={filter?.disableClearFilterMessage || t('somethingWentWrong')}>
                        <CloseIcon />
                      </span>
                    ) : (
                      <span
                        className="close-icon"
                        role="presentation"
                        onClick={() => handleRemoveFilterByKey(filter.key)}>
                        <CloseIcon />
                      </span>
                    )}
                  </div>
                );
              } else if (
                filter.applyFilter &&
                filter.type === filterType.ANGLE_BRACKETS &&
                filter?.value1 &&
                filter?.value2
              ) {
                filterTag = (
                  <div className={`filter-tag-wrap ${filter.type && 'angleBrackets'}`}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details-date">
                      <p>{t(filter.name)}: </p>
                      <p>{filter.value2}</p>
                      <p>{filter.value1}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (
                filter.applyFilter &&
                Array.isArray(filter?.value) &&
                filter.type === filterType.MULTISELECT
              ) {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value.join(', ')}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{filter.name}: </h6>
                      {filter?.valueToShow ? <p>{filter?.valueToShow.join(', ')}</p> : <p />}
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (filter.applyFilter && Array.isArray(filter.value)) {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value.join(', ')}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{filter.name}: </h6>
                      <p>{filter.value.join(', ')}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              } else if (filter.applyFilter && typeof filter.value === 'string') {
                filterTag = (
                  <div className="filter-tag-wrap" key={filter.value}>
                    <span className="filter-icon">
                      <Filter />
                    </span>
                    <div className="filter-tag-details">
                      <h6>{filter.name}: </h6>
                      <p>{filter.value}</p>
                    </div>
                    <span
                      className="close-icon"
                      role="presentation"
                      onClick={() => handleRemoveFilterByKey(filter.key)}>
                      <CloseIcon />
                    </span>
                  </div>
                );
              }
              return filterTag;
            })}
        </div>
      )}
    </div>
  );
}

export default CustomTableHeader;
